import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { useSeo, PROJECT_TYPES, SERVICE_LEVELS, BUDGETS, TIMEFRAMES, SUBURBS } from "@/lib/site";
import { MaskLines } from "@/components/Reveal";

const STEPS = ["Project", "Block & size", "Site & photos", "Contact"];

export default function Quote() {
  useSeo("Get a Quote — Breeze Craft Sydney", "Request a breeze block supply or install quote. Photos welcome, reply within one business day.");
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    project_type: "", service_level: "", block_interest: "", size: "",
    suburb: params.get("suburb") || "", budget: "", timeframe: "", message: "",
    name: "", email: "", phone: "", photos: [],
  });

  useEffect(() => {
    api.get("/blocks").then((r) => {
      setBlocks(r.data);
      const pre = params.get("block");
      if (pre) {
        const found = r.data.find((b) => b.slug === pre);
        const colour = params.get("colour");
        if (found) setForm((f) => ({ ...f, block_interest: colour && found.colours?.includes(colour) ? `${found.name} — ${colour}` : found.name }));
      }
    }).catch(() => {});
    const svc = params.get("service");
    if (svc) setForm((f) => ({ ...f, service_level: svc }));
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.project_type && !!form.service_level;
    if (step === 1) return true;
    if (step === 2) return !!form.suburb.trim();
    return true;
  };

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, 5 - form.photos.length);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads", fd);
        setForm((f) => ({ ...f, photos: [...f.photos, r.data.path] }));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        service_level: SERVICE_LEVELS.find((s) => s.id === form.service_level)?.label || form.service_level,
      };
      const r = await api.post("/leads", payload);
      setDone(r.data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center" data-testid="quote-confirmation">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto w-16 h-16 bg-terracotta text-white flex items-center justify-center">
          <Check size={28} />
        </motion.div>
        <h1 className="mt-8 font-display text-4xl sm:text-5xl font-medium tracking-tight text-ink">Request received.</h1>
        <p className="mt-4 text-neutral-600 leading-relaxed max-w-md mx-auto">
          Your reference is <span className="font-mono text-terracotta">{done.slice(0, 8).toUpperCase()}</span>.
          We've emailed you a confirmation and the team will be in touch within one business day.
        </p>
      </div>
    );
  }

  const inputCls = "w-full bg-white border border-ink/15 px-4 py-3 text-sm text-ink focus:outline-none focus:border-terracotta transition-colors";
  const optionCls = (active) =>
    `text-left border px-5 py-4 transition-all duration-200 ${active ? "border-terracotta bg-terracotta/5" : "border-ink/15 bg-white hover:border-ink/40"}`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="quote-page">
      <p className="kicker mb-4">Get a quote</p>
      <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-ink" data-testid="quote-heading">
        <MaskLines lines={["Five minutes now,", "one call back."]} />
      </h1>

      <div className="mt-10 flex items-center gap-2" data-testid="quote-progress">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1 transition-colors duration-300 ${i <= step ? "bg-terracotta" : "bg-ink/10"}`} />
            <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.15em] ${i === step ? "text-terracotta" : "text-neutral-400"}`}>
              {i + 1}. {s}
            </p>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.3 }}
          className="mt-10"
        >
          {step === 0 && (
            <div data-testid="quote-step-1">
              <p className="font-display text-xl font-medium text-ink mb-4">What are you building?</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PROJECT_TYPES.map((p) => (
                  <button key={p} data-testid={`quote-project-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => set("project_type", p)} className={optionCls(form.project_type === p)}>
                    <span className="text-sm font-medium text-ink">{p}</span>
                  </button>
                ))}
              </div>
              <p className="font-display text-xl font-medium text-ink mt-10 mb-4">How do you want it done?</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SERVICE_LEVELS.map((s) => (
                  <button key={s.id} data-testid={`quote-service-${s.id}`} onClick={() => set("service_level", s.id)} className={optionCls(form.service_level === s.id)}>
                    <span className="text-sm font-semibold text-ink block">{s.label}</span>
                    <span className="text-xs text-neutral-500 mt-1 block leading-relaxed">{s.blurb}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="quote-step-2">
              <p className="font-display text-xl font-medium text-ink mb-4">Any block style caught your eye?</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {blocks.map((b) => (
                  <button key={b.slug} data-testid={`quote-block-${b.slug}`} onClick={() => set("block_interest", form.block_interest.split(" — ")[0] === b.name ? "" : b.name)} className={optionCls(form.block_interest.split(" — ")[0] === b.name) + " !px-3 !py-3"}>
                    <div className="aspect-square overflow-hidden mb-2">
                      <img src={imgSrc(b.images[0])} alt={b.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-ink">{b.name}</span>
                  </button>
                ))}
              </div>
              <p className="font-display text-xl font-medium text-ink mt-10 mb-4">Rough size?</p>
              <input
                data-testid="quote-size-input"
                className={inputCls}
                placeholder='e.g. "Front fence, about 12m long x 1.8m high" or "4 x 3m feature wall"'
                value={form.size}
                onChange={(e) => set("size", e.target.value)}
              />
            </div>
          )}

          {step === 2 && (
            <div data-testid="quote-step-3">
              <p className="font-display text-xl font-medium text-ink mb-4">Where's the site?</p>
              <input
                data-testid="quote-suburb-input"
                className={inputCls}
                placeholder="Suburb — e.g. Bondi, Marrickville, Castle Hill"
                value={form.suburb}
                onChange={(e) => set("suburb", e.target.value)}
                list="suburb-suggestions"
              />
              <datalist id="suburb-suggestions">
                {SUBURBS.map((s) => <option key={s.slug} value={s.name} />)}
              </datalist>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                <div>
                  <p className="font-display text-lg font-medium text-ink mb-3">Budget range</p>
                  <div className="flex flex-wrap gap-2">
                    {BUDGETS.map((b) => (
                      <button key={b} data-testid={`quote-budget-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => set("budget", b)} className={optionCls(form.budget === b) + " !px-4 !py-2.5"}>
                        <span className="text-sm text-ink">{b}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-display text-lg font-medium text-ink mb-3">Timeframe</p>
                  <div className="flex flex-wrap gap-2">
                    {TIMEFRAMES.map((t) => (
                      <button key={t} data-testid={`quote-timeframe-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => set("timeframe", t)} className={optionCls(form.timeframe === t) + " !px-4 !py-2.5"}>
                        <span className="text-sm text-ink">{t}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="font-display text-lg font-medium text-ink mt-10 mb-3">Site photos <span className="text-sm font-normal text-neutral-500">(optional, up to 5)</span></p>
              <div className="flex flex-wrap gap-3" data-testid="quote-photo-upload">
                {form.photos.map((p) => (
                  <div key={p} className="relative w-24 h-24">
                    <img src={imgSrc(p)} alt="Site upload" className="w-full h-full object-cover border border-ink/10" />
                    <button
                      data-testid={`quote-photo-remove-${p.slice(-8)}`}
                      onClick={() => set("photos", form.photos.filter((x) => x !== p))}
                      className="absolute -top-2 -right-2 bg-ink text-bone p-1"
                      aria-label="Remove photo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {form.photos.length < 5 && (
                  <button
                    data-testid="quote-photo-add-button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-24 h-24 border border-dashed border-ink/30 flex flex-col items-center justify-center gap-1 text-neutral-500 hover:border-terracotta hover:text-terracotta transition-colors"
                  >
                    <ImagePlus size={20} />
                    <span className="text-[10px] font-mono uppercase tracking-wider">{uploading ? "Uploading" : "Add"}</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple className="hidden" data-testid="quote-photo-file-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
              </div>
              <textarea
                data-testid="quote-message-input"
                className={inputCls + " mt-8 min-h-[100px]"}
                placeholder="Anything else — access, slope, existing footings, council requirements…"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
            </div>
          )}

          {step === 3 && (
            <div data-testid="quote-step-4">
              <p className="font-display text-xl font-medium text-ink mb-6">Where do we send the quote?</p>
              <div className="space-y-4">
                <input data-testid="quote-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="quote-email-input" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="quote-phone-input" type="tel" className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="mt-8 border border-ink/10 bg-bone-dark p-5" data-testid="quote-summary">
                <p className="kicker mb-3">Summary</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <span className="text-neutral-500">Project</span><span className="text-ink text-right">{form.project_type || "—"}</span>
                  <span className="text-neutral-500">Service</span><span className="text-ink text-right">{SERVICE_LEVELS.find((s) => s.id === form.service_level)?.label || "—"}</span>
                  <span className="text-neutral-500">Block</span><span className="text-ink text-right">{form.block_interest || "No preference"}</span>
                  <span className="text-neutral-500">Size</span><span className="text-ink text-right">{form.size || "—"}</span>
                  <span className="text-neutral-500">Suburb</span><span className="text-ink text-right">{form.suburb}</span>
                  <span className="text-neutral-500">Budget</span><span className="text-ink text-right">{form.budget || "—"}</span>
                  <span className="text-neutral-500">Photos</span><span className="text-ink text-right">{form.photos.length} attached</span>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-12 flex items-center justify-between">
        <button
          data-testid="quote-back-button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-ink disabled:opacity-30 transition-colors"
        >
          <ArrowLeft size={16} /> Back
        </button>
        {step < 3 ? (
          <button
            data-testid="quote-next-button"
            onClick={() => canNext() && setStep((s) => s + 1)}
            disabled={!canNext()}
            className="inline-flex items-center gap-2 bg-ink text-bone px-7 py-3.5 text-sm font-semibold hover:bg-terracotta disabled:opacity-40 transition-colors"
          >
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button
            data-testid="quote-submit-button"
            onClick={submit}
            disabled={submitting || !form.name.trim() || !form.email.trim() || !form.phone.trim()}
            className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark disabled:opacity-40 transition-colors"
          >
            {submitting ? "Sending…" : "Send quote request"} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
